import React from 'react';
import { InputMeta, InputSelect, ModifiedInput } from '.';
import cn from 'classnames';
import './inputPhone.scss';
import { SupportedLanguagesShort } from '../../../utils/localization';
import { InputWrapper } from './InputWrapper';

interface InputPhoneProps extends ModifiedInput {
  defaultCountryCode: SupportedLanguagesShort;
  isLoading?: boolean;
  onChange: (value: string) => void;
  meta?: InputMeta;
  value: string;
}

export const InputPhone: React.FC<InputPhoneProps> = ({
  defaultCountryCode,
  onChange,
  className,
  value,
  meta,
  ...unused
}) => {
  const { phoneNumber, landCode } = parsePhoneNumber(value as string, defaultCountryCode);
  const save = (pn: string, newC: string): void => {
    const normNumber = normalizePhoneNumber(pn, newC, landCode);
    onChange(normNumber);
  };

  return (
    <InputWrapper {...meta}>
      <div className={cn('input-phone flex', 'w-full', className)}>
        <InputSelect
          addEmptyOption={false}
          value={landCode}
          onChange={newLandCode => save(phoneNumber, newLandCode)}
          options={countries.map(c => {
            return {
              label: `${c.flag} ${c.landCode}`,
              value: c.landCode,
            };
          })}
        />
        <input
          type="text"
          className="input-default rounded-r"
          value={phoneNumber}
          onChange={(event: React.FormEvent<HTMLInputElement>): void => {
            save(event.currentTarget.value, landCode);
          }}
          {...unused}
        />
      </div>
    </InputWrapper>
  );
};

const parsePhoneNumber = (value: string, defaultCountryCode: string): { landCode: string; phoneNumber: string } => {
  let landCode = countries.find(c => c.countryCodeShort === defaultCountryCode)?.landCode || '+31';
  let phoneNumber = '';
  countries.forEach(c => {
    if ((value || '').includes(c.landCode)) {
      landCode = c.landCode;
      phoneNumber = value.replace(c.landCode, '').trim();
    }
  });
  // When no matching coutry/landcode, we end up with a empty phonenumber, let's try to format it
  if (value && !phoneNumber) {
    return { landCode, phoneNumber: formatPhoneNumber(landCode, value) };
  }
  return { landCode, phoneNumber: `${landCode} ${phoneNumber}` };
};

const normalizePhoneNumber = (phoneNumber: string, landCode: string, oldLandCode?: string): string => {
  let number = phoneNumber.trim();
  if (oldLandCode) {
    number = number.replace(oldLandCode, '').trim();
  }
  // If Landcode was found in above cases we didn't came here.
  // Accept default landcode
  if (landCode) return formatPhoneNumber(landCode, number);
  // We don't know, so hopefully the user knows
  return phoneNumber;
};

const formatPhoneNumber = (landCode: string, phoneNumber: string): string => {
  const code = landCode.trim();
  let number = phoneNumber.trim();

  // Remove any occurrences of the land code (with leading "+" or "00") from the phone number
  // For example, for landCode "+31", also remove "0031"
  const codeRegex = new RegExp(`^(\\+|00)?${code.replace(/^\+/, '')}`);
  number = number.replace(codeRegex, '').trim();

  // Remove all non-digit characters except leading zeros and spaces (allow spaces in number for readability)
  number = number.replace(/[^\d\s]/g, '');

  if (code === '+31') {
    // dutch numbers shouldn't have a leading zero, when the landcode is included
    return `${code} ${number.replace(/^0+/, '')}`;
  }
  return `${code} ${number}`;
};

const countries = [
  {
    countryCodeShort: 'nl',
    flag: '🇳🇱',
    landCode: '+31',
  },
  {
    countryCodeShort: 'be',
    flag: '🇧🇪',
    landCode: '+32',
  },
  {
    countryCodeShort: 'de',
    flag: '🇩🇪',
    landCode: '+49',
  },
  {
    countryCodeShort: 'at',
    flag: '🇦🇹',
    landCode: '+43',
  },
  {
    countryCodeShort: 'ch',
    flag: '🇨🇭',
    landCode: '+41',
  },
  {
    countryCodeShort: 'dk',
    flag: '🇩🇰',
    landCode: '+45',
  },
  {
    countryCodeShort: 'es',
    flag: '🇪🇸',
    landCode: '+34',
  },
  {
    countryCodeShort: 'fr',
    flag: '🇫🇷',
    landCode: '+33',
  },
  {
    countryCodeShort: 'gb',
    flag: '🇬🇧',
    landCode: '+44',
  },
  {
    countryCodeShort: 'se',
    flag: '🇸🇪',
    landCode: '+46',
  },
  {
    countryCodeShort: 'ie',
    flag: '🇮🇪',
    landCode: '+353',
  },
  {
    countryCodeShort: 'it',
    flag: '🇮🇹',
    landCode: '+39',
  },
];
